// Spin the Bottle on screen (desktop + phone): the join prompt, the lobby with
// the options (mode, spice, rounds), and the game — whose turn, the card,
// Spin / Truth / Dare / Done / Pass / Clap / Skip, scores, vote to end and,
// for the world owner, removing a player. VR uses BottleVRPanel.

import React, { useState } from 'react';
import { tableAction } from '../gameStore';
import { JoinPrompt, SeatList, LobbyActions, HelpButton, GameOverBlock, PanelError, TurnTimer, leaveTable } from '../kit/LobbyKit';
import { Leaderboard } from '../Leaderboard';
import { ROUND_CHOICES, type BottleOptions } from '../../../../supabase/functions/games/bottle.ts';
import type { OverlayProps } from '../types';
import {
  type BottleState, OPTION_LABELS, roundsLabel, statusLine, lastLine, scoreRows, winnerLine, sendMove,
  playerCount, majorityOf, useIsWorldOwner,
} from './bottleUi';
import './BottleOverlay.css';

function Seg<T extends string | number>({ label, value, options, onPick, disabled }: {
  label: string; value: T; options: { v: T; label: string }[]; onPick: (v: T) => void; disabled?: boolean;
}) {
  return (
    <div className="gx-bt-opt" role="group" aria-label={label}>
      <span className="gx-bt-opt-label">{label}</span>
      <div className="gx-bt-seg">
        {options.map((o) => (
          <button key={String(o.v)} className={`gx-bt-seg-btn ${o.v === value ? 'is-on' : ''}`} aria-pressed={o.v === value}
            disabled={disabled} onClick={() => { if (o.v !== value) onPick(o.v); }}>{o.label}</button>
        ))}
      </div>
    </div>
  );
}

function Lobby(props: OverlayProps) {
  const { def, pid, entry, table, worldId, onHelp } = props;
  const [board, setBoard] = useState(false);
  const o: BottleOptions = { mode: 'tod', spice: 'mild', rounds: 0, ...(table.state.options ?? {}) };
  const set = (patch: Partial<BottleOptions>) => void tableAction(pid, 'configure', { options: { ...o, ...patch } });
  return (
    <div className="gx-sp gx-sp--panel gx-bt" role="region" aria-label="Spin the Bottle lobby">
      <div className="gx-sp-head">
        <span className="gx-sp-prompt-title"><span className="gx-sp-spade">{def.glyph}</span> {def.label}</span>
        <div className="gx-sp-head-actions">
          <button className={`gx-sp-btn gx-sp-btn--ghost ${board ? 'is-on' : ''}`} onClick={() => setBoard((v) => !v)} aria-pressed={board}>Leaderboard</button>
          <button className="gx-sp-btn gx-sp-btn--ghost" onClick={() => leaveTable(pid)}>Leave</button>
        </div>
      </div>
      {board ? <Leaderboard kind={def.kind} worldId={worldId} compact title={false} limit={8} /> : <>
        <div className="gx-bt-opts">
          <Seg label="Mode" value={o.mode} disabled={entry.busy} onPick={(mode) => set({ mode })}
            options={[{ v: 'tod', label: OPTION_LABELS.mode.tod }, { v: 'spin', label: OPTION_LABELS.mode.spin }]} />
          <Seg label="Spice" value={o.spice} disabled={entry.busy} onPick={(spice) => set({ spice })}
            options={[{ v: 'mild', label: 'Mild' }, { v: 'silly', label: 'Silly' }]} />
          <Seg label="Length" value={o.rounds} disabled={entry.busy} onPick={(rounds) => set({ rounds })}
            options={ROUND_CHOICES.map((r) => ({ v: r as number, label: r === 0 ? 'Endless' : String(r) }))} />
        </div>
        <p className="gx-sp-note">
          {o.mode === 'tod' ? 'The bottle picks who answers; they choose Truth or Dare.' : 'The bottle picks who answers; the spinner chooses Truth or Dare for them.'}
          {' '}{o.rounds ? `${roundsLabel(o.rounds)} — everyone gets about ${o.rounds} turn${o.rounds === 1 ? '' : 's'}.` : 'Plays until a majority votes to end.'}
        </p>
        <SeatList {...props} />
      </>}
      <LobbyActions {...props} />
      <p className="gx-sp-note">3–12 players · friends can join any time. <HelpButton def={def} onHelp={onHelp} /></p>
      <PanelError entry={entry} />
    </div>
  );
}

export function BottleOverlay(props: OverlayProps) {
  const { def, pid, entry, table, seat, me, worldId, onHelp } = props;
  const owner = useIsWorldOwner(worldId, me);
  const [showPlayers, setShowPlayers] = useState(false);
  const st = table.state;

  if (seat < 0) return <JoinPrompt {...props} />;
  if (st.phase === 'lobby') return <Lobby {...props} />;
  const g = st.g as BottleState | null;
  if (!g) return null;

  const busy = entry.busy;
  const playing = st.phase === 'playing';
  const n = playerCount(st);
  const need = majorityOf(n);
  const iVotedEnd = g.endVotes.includes(seat);
  const iVotedSkip = g.skipVotes.includes(seat);
  const iClapped = g.claps.includes(seat);
  const amTarget = g.target === seat;
  const me_ = g.players[seat];
  const passesLeft = Math.max(0, g.passLimit - (me_?.passes ?? 0));
  const rows = scoreRows(st, g);
  const last = lastLine(g);
  const clock = playing && (g.phase === 'spin' || g.phase === 'choose' || g.phase === 'card') ? g.endsAt : null;
  const roundText = g.rounds ? `Round ${Math.min(g.round, g.rounds)}/${g.rounds}` : `Endless · turn ${g.turnNo + 1}`;
  const myTurn = playing && ((g.phase === 'spin' && g.spinner === seat) || (g.phase === 'choose' && g.chooser === seat) || (g.phase === 'card' && amTarget));

  return (
    <div className={`gx-sp gx-sp--panel gx-sp--game gx-bt ${myTurn ? 'is-my-turn' : ''}`} role="region" aria-label="Spin the Bottle game">
      <div className="gx-sp-head">
        <span className="gx-bt-round">{def.glyph} {roundText} · {OPTION_LABELS.spice[g.spice]}</span>
        <div className="gx-sp-head-actions">
          <button className={`gx-sp-btn gx-sp-btn--ghost ${showPlayers ? 'is-on' : ''}`} onClick={() => setShowPlayers((v) => !v)} aria-pressed={showPlayers}>Scores</button>
          <HelpButton def={def} onHelp={onHelp} compact />
          <button className="gx-sp-btn gx-sp-btn--ghost" onClick={() => leaveTable(pid)}>Leave</button>
        </div>
      </div>

      {st.phase === 'over' ? (
        <>
          <ol className="gx-bt-scores">
            {rows.map((r) => <li key={r.seat} className={r.seat === seat ? 'is-me' : ''}><span>{r.name}{r.seat === seat ? ' (you)' : ''}</span><b>{r.score}</b></li>)}
          </ol>
          <GameOverBlock {...props} summary={winnerLine(st, g, seat)} />
        </>
      ) : (
        <>
          <div className="gx-bt-status" aria-live="polite">
            <span className={myTurn ? 'is-mine' : ''}>{statusLine(st, g, seat)}</span>
            {clock && <TurnTimer deadline={clock} warnAt={g.phase === 'card' ? 15 : 8} />}
          </div>

          {(g.phase === 'card' || g.phase === 'cheer') && g.card && (
            <div className={`gx-bt-card is-${g.card.type}`}>
              <span className="gx-bt-card-type">{g.card.type === 'dare' ? 'Dare' : 'Truth'}</span>
              <p>{g.card.text}</p>
            </div>
          )}

          <div className="gx-bt-actions">
            {g.phase === 'spin' && g.spinner === seat && (
              <button className="gx-sp-btn gx-sp-btn--primary gx-bt-big" disabled={busy} onClick={() => void sendMove(pid, { type: 'spin', power: 0.6 + Math.random() * 0.4 })}>Spin the bottle</button>
            )}
            {g.phase === 'choose' && g.chooser === seat && <>
              <button className="gx-sp-btn gx-bt-big gx-bt-truth" disabled={busy} onClick={() => void sendMove(pid, { type: 'choose', pick: 'truth' })}>Truth <small>+1</small></button>
              <button className="gx-sp-btn gx-bt-big gx-bt-dare" disabled={busy} onClick={() => void sendMove(pid, { type: 'choose', pick: 'dare' })}>Dare <small>+2</small></button>
            </>}
            {g.phase === 'card' && amTarget && <>
              <button className="gx-sp-btn gx-sp-btn--primary gx-bt-big" disabled={busy} onClick={() => void sendMove(pid, { type: 'done' })}>Done ✓</button>
              <button className="gx-sp-btn gx-bt-big" disabled={busy || passesLeft <= 0} onClick={() => void sendMove(pid, { type: 'pass' })}>
                Pass <small>{passesLeft} left</small>
              </button>
            </>}
            {(g.phase === 'card' || g.phase === 'cheer') && !amTarget && g.target !== null && (
              <button className="gx-sp-btn gx-bt-big" disabled={busy || iClapped} onClick={() => void sendMove(pid, { type: 'clap' })}>
                {iClapped ? 'Clapped 👏' : '👏 Clap'}
              </button>
            )}
            {g.phase === 'card' && (
              <button className="gx-sp-btn gx-sp-btn--ghost" disabled={busy || iVotedSkip} onClick={() => void sendMove(pid, { type: 'skip' })}
                title="Not OK with this card? A majority vote swaps it for another.">
                Skip card {g.skipVotes.length ? `(${g.skipVotes.length}/${need})` : ''}
              </button>
            )}
          </div>

          {last && <span className="gx-sp-note">{last}</span>}
        </>
      )}

      {showPlayers && st.phase !== 'over' && (
        <ol className="gx-bt-scores">
          {rows.map((r) => (
            <li key={r.seat} className={r.seat === seat ? 'is-me' : ''}>
              <span>{r.name}{r.seat === seat ? ' (you)' : ''} <small>{Math.max(0, g.passLimit - r.passes)} passes</small></span>
              <b>{r.score}</b>
              {owner && r.seat !== seat && (
                <button className="gx-sp-link" disabled={busy} onClick={() => { if (window.confirm(`Remove ${r.name} from the game?`)) void tableAction(pid, 'kick', { seat: r.seat }); }}>remove</button>
              )}
            </li>
          ))}
        </ol>
      )}

      {playing && (
        <div className="gx-bt-foot">
          <button className={`gx-sp-link ${iVotedEnd ? 'is-on' : ''}`} disabled={busy}
            onClick={() => void sendMove(pid, { type: 'end', on: !iVotedEnd })}>
            {iVotedEnd ? 'Keep playing' : 'Vote to end'}
          </button>
          {g.endVotes.length > 0 && <span className="gx-sp-note">{g.endVotes.length}/{need} want to end</span>}
          <span className="gx-sp-note">You: {me_?.score ?? 0} pts</span>
        </div>
      )}
      <PanelError entry={entry} />
    </div>
  );
}

export default BottleOverlay;
